import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./menue.css";

export type MenuePunkt = { text: string; symbol?: ReactNode; aktion: () => void; gefahr?: boolean; deaktiviert?: boolean; tasten?: string } | "trenner";

/** Freischwebende Menüliste an einem Punkt; schließt bei Escape, Klick daneben und nach einer Auswahl. */
export function MenueListe({ punkte, x, y, onSchliessen, label }: { punkte: MenuePunkt[]; x: number; y: number; onSchliessen: () => void; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [lage, setLage] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setLage({ left: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)), top: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)) });
    el.querySelector<HTMLButtonElement>("[role=menuitem]:not([disabled])")?.focus();
  }, [x, y]);

  useEffect(() => {
    const daneben = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onSchliessen();
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onSchliessen();
      }
    };
    document.addEventListener("pointerdown", daneben, true);
    document.addEventListener("keydown", taste);
    window.addEventListener("resize", onSchliessen);
    return () => {
      document.removeEventListener("pointerdown", daneben, true);
      document.removeEventListener("keydown", taste);
      window.removeEventListener("resize", onSchliessen);
    };
  }, [onSchliessen]);

  const pfeile = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const eintraege = [...(ref.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]:not([disabled])") ?? [])];
    const i = eintraege.indexOf(document.activeElement as HTMLButtonElement);
    eintraege[(i + (e.key === "ArrowDown" ? 1 : -1) + eintraege.length) % eintraege.length]?.focus();
  };

  return createPortal(
    <div ref={ref} className="menue" role="menu" aria-label={label} style={lage} onKeyDown={pfeile}>
      {punkte.map((p, i) =>
        p === "trenner" ? (
          <div key={i} className="menue-trenner" role="separator" />
        ) : (
          <button
            key={i}
            type="button"
            role="menuitem"
            className={`menue-punkt${p.gefahr ? " menue-gefahr" : ""}`}
            disabled={p.deaktiviert}
            onClick={() => {
              onSchliessen();
              p.aktion();
            }}
          >
            <span className="menue-symbol">{p.symbol}</span>
            <span>{p.text}</span>
            {p.tasten && <kbd>{p.tasten}</kbd>}
          </button>
        ),
      )}
    </div>,
    document.body,
  );
}

export function MenueKnopf({ punkte, label, children, className = "knopf knopf-still knopf-symbol" }: { punkte: MenuePunkt[]; label: string; children: ReactNode; className?: string }) {
  const [offen, setOffen] = useState<{ x: number; y: number } | null>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={knopf}
        type="button"
        className={className}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={!!offen}
        onClick={(e) => {
          e.stopPropagation();
          const r = e.currentTarget.getBoundingClientRect();
          setOffen(offen ? null : { x: r.right - 180, y: r.bottom + 4 });
        }}
      >
        {children}
      </button>
      {offen && (
        <MenueListe
          punkte={punkte}
          x={offen.x}
          y={offen.y}
          label={label}
          onSchliessen={() => {
            setOffen(null);
            knopf.current?.focus();
          }}
        />
      )}
    </>
  );
}
